/**
 * Painel de seleção de produtos do kit — padrão mscad-equip / deq.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { IconBuilding, IconCirclesRelation, IconFilterPlus, IconPrinter, IconStack2, IconUserCheck } from '@tabler/icons-react';
import { BoardToolbarExpandSearch } from '../../board/toolbar/BoardToolbarExpandSearch';
import { BoardSearchSimplePickerPanel } from '../../board/board-search-filters/BoardSearchSimplePickerPanel';
import { EcClickUpFilterChip } from '../../experiencia-cliente/components/EcClickUpFilterChip';
import {
  buildEcFilterPopoverStyle,
  EC_FILTER_POPOVER_CLASS,
  useEcFilterPopoverLayout,
} from '../../experiencia-cliente/components/experienciaClienteFilterPopoverUtils';
import { DeqEquipTecnologiaFilterPanel } from '../../process-config/components/DeqEquipTecnologiaFilterPanel';
import {
  fetchKitCompatibleModels,
  fetchKitEquipamentos,
  fetchKitFabricantes,
  fetchKitProductStocks,
  fetchKitProdutos,
  fetchKitTecnologias,
} from '../services/kitTecnicoService';
import '../../board/toolbar/BoardClickUpFilterChip.css';
import '../../board/board-search-filters/BoardSearchFilters.css';
import '../../board/toolbar/BoardToolbarSearchFilters.css';
import '../../process-config/components/DefeitosEquipCompatModal.css';
import '../../process-config/components/MarcaSubgruposCadastrosConfig.css';
import './KitCreateProductsStep.css';

/* Acima do modal Criar Kit (backdrop board-encaminhar = 28000) e do !important de deq-filter-popover */
const KIT_FILTER_POPOVER_Z = 29050;

const AVATAR_PALETTES = [
  { bg: '#e0f2fe', text: '#0369a1' },
  { bg: '#fef3c7', text: '#b45309' },
  { bg: '#d1fae5', text: '#047857' },
  { bg: '#ffe4e6', text: '#be123c' },
  { bg: '#ffedd5', text: '#c2410c' },
  { bg: '#dbeafe', text: '#1d4ed8' },
];

function withPopoverStyle(layoutStyle) {
  if (!layoutStyle) return null;
  return { ...layoutStyle, zIndex: KIT_FILTER_POPOVER_Z };
}

function marcaInitials(nome) {
  const s = String(nome || '').trim();
  if (!s) return '?';
  const parts = s.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}

function paletteForMarca(name) {
  const s = String(name || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h + s.charCodeAt(i)) % AVATAR_PALETTES.length;
  return AVATAR_PALETTES[h];
}

function MarcaAvatar({ fabricante }) {
  const p = paletteForMarca(fabricante);
  return (
    <span className="deq-avatar" style={{ background: p.bg, color: p.text }}>
      {marcaInitials(fabricante)}
    </span>
  );
}

function KitCheckbox({ checked, onClick, title }) {
  return (
    <label className="deq-check-label" title={title} onClick={(e) => e.stopPropagation()}>
      <input
        type="checkbox"
        className="deq-check-native"
        checked={checked}
        onChange={() => onClick()}
        onClick={(e) => e.stopPropagation()}
        aria-label={title}
      />
      <span className="deq-check-box" aria-hidden />
    </label>
  );
}

function FiltersEmptyState() {
  return (
    <div className="deq-empty deq-empty--modern">
      <div className="deq-empty-placeholder">
        <IconFilterPlus size={40} stroke={1.25} className="deq-empty-icon" aria-hidden />
        <h3 className="deq-empty-modern-title">Aplique os filtros</h3>
        <p className="deq-empty-modern-desc">
          Escolha fabricante, tecnologia e/ou equipamento, ou digite na busca. Os filtros se
          ajustam entre si conforme o que você escolher.
        </p>
      </div>
    </div>
  );
}

function ProductSubmeta({ cod, referencia }) {
  const code = String(cod || '').trim();
  const ref = String(referencia || '').trim();
  const hostRef = useRef(null);
  const [tip, setTip] = useState(null);

  const showTip = () => {
    if (!code || !hostRef.current) return;
    const r = hostRef.current.getBoundingClientRect();
    const preferAbove = r.top > 72;
    setTip({
      left: Math.min(r.right, window.innerWidth - 12),
      top: preferAbove ? r.top - 8 : r.bottom + 8,
      place: preferAbove ? 'above' : 'below',
    });
  };

  const hideTip = () => setTip(null);

  if (!code && !ref) return null;

  return (
    <span className="kit-product-submeta">
      {ref ? (
        <span
          ref={hostRef}
          className="kit-product-ref-tip-host"
          tabIndex={code ? 0 : undefined}
          aria-label={code ? `Referência ${ref}. Código interno ${code}` : `Referência ${ref}`}
          onMouseEnter={showTip}
          onMouseLeave={hideTip}
          onFocus={showTip}
          onBlur={hideTip}
          onClick={(e) => e.stopPropagation()}
        >
          <span className="kit-product-submeta-ref">{ref}</span>
        </span>
      ) : code ? (
        <span className="kit-linked-meta kit-linked-meta--code-only">{code}</span>
      ) : null}
      {tip && code
        ? ReactDOM.createPortal(
            <span
              className={`kit-product-ref-tip kit-product-ref-tip--portal kit-product-ref-tip--${tip.place}`}
              role="tooltip"
              style={{ top: tip.top, left: tip.left }}
            >
              <span className="kit-product-ref-tip-label">Código interno</span>
              <span className="kit-product-ref-tip-value">{code}</span>
            </span>,
            document.body
          )
        : null}
      {code ? <CompatibleModelsButton productCode={code} /> : null}
    </span>
  );
}

/**
 * Botão + popover: modelos de equipamento compatíveis com a peça.
 */
function CompatibleModelsButton({ productCode }) {
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [models, setModels] = useState(null);
  const [pos, setPos] = useState(null);
  const [hoverTip, setHoverTip] = useState(null);

  const placeHoverTip = useCallback(() => {
    const el = btnRef.current;
    if (!el || open) {
      setHoverTip(null);
      return;
    }
    const r = el.getBoundingClientRect();
    /* Preferir abaixo na 1ª linha / topo do scroll, para não cortar no brand sticky */
    const preferBelow = r.top < 140;
    setHoverTip({
      left: r.left + r.width / 2,
      top: preferBelow ? r.bottom + 8 : r.top - 8,
      place: preferBelow ? 'below' : 'above',
    });
  }, [open]);

  const hideHoverTip = useCallback(() => setHoverTip(null), []);

  const placePopover = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = 320;
    const pad = 8;
    let left = r.right - width;
    if (left < pad) left = pad;
    if (left + width > window.innerWidth - pad) left = window.innerWidth - width - pad;
    const spaceBelow = window.innerHeight - r.bottom;
    const preferBelow = spaceBelow > 200 || r.top < 180;
    setPos({
      left,
      top: preferBelow ? r.bottom + 8 : r.top - 8,
      place: preferBelow ? 'below' : 'above',
      width,
    });
  }, []);

  const loadModels = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchKitCompatibleModels(productCode);
      setModels(list);
    } catch (err) {
      setModels([]);
      setError(err?.message || 'Erro ao carregar modelos');
    } finally {
      setLoading(false);
    }
  }, [productCode]);

  const modelGroups = useMemo(() => {
    if (!Array.isArray(models) || !models.length) return [];
    const map = new Map();
    for (const m of models) {
      const fab = String(m.fabricante || '').trim() || 'Sem fabricante';
      const name = String(m.equipment_name || '').trim();
      if (!name) continue;
      if (!map.has(fab)) map.set(fab, []);
      const list = map.get(fab);
      if (!list.includes(name)) list.push(name);
    }
    return Array.from(map.entries())
      .map(([fabricante, names]) => ({
        fabricante,
        names: names.sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' })),
      }))
      .sort((a, b) =>
        a.fabricante.localeCompare(b.fabricante, 'pt-BR', { sensitivity: 'base' })
      );
  }, [models]);

  const toggleOpen = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setHoverTip(null);
    setOpen((prev) => {
      if (!prev) {
        placePopover();
        return true;
      }
      return false;
    });
  };

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      await loadModels();
      if (!cancelled) placePopover();
    })();
    return () => {
      cancelled = true;
    };
  }, [open, loadModels, placePopover]);

  /* Trava scroll das listas disponíveis / no kit enquanto o popover está aberto */
  useEffect(() => {
    if (!open) return undefined;
    const scrolls = Array.from(
      document.querySelectorAll(
        '.kit-products-equip-panel .mscad-scroll--available, .kit-products-equip-panel .mscad-scroll--linked, .kit-products-equip-panel .deq-scroll'
      )
    );
    const prev = scrolls.map((el) => ({
      el,
      overflowY: el.style.overflowY,
      overflowX: el.style.overflowX,
    }));
    scrolls.forEach((el) => {
      el.style.overflowY = 'hidden';
      el.style.overflowX = 'hidden';
      el.classList.add('kit-scroll-locked');
    });

    const allowInPop = (target) =>
      Boolean(popRef.current?.contains(target) && target.closest?.('.kit-compat-models-pop-body'));

    const blockScroll = (ev) => {
      if (allowInPop(ev.target)) return;
      ev.preventDefault();
    };

    document.addEventListener('wheel', blockScroll, { passive: false, capture: true });
    document.addEventListener('touchmove', blockScroll, { passive: false, capture: true });

    return () => {
      prev.forEach(({ el, overflowY, overflowX }) => {
        el.style.overflowY = overflowY;
        el.style.overflowX = overflowX;
        el.classList.remove('kit-scroll-locked');
      });
      document.removeEventListener('wheel', blockScroll, true);
      document.removeEventListener('touchmove', blockScroll, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (ev) => {
      if (btnRef.current?.contains(ev.target) || popRef.current?.contains(ev.target)) return;
      setOpen(false);
    };
    const onKey = (ev) => {
      if (ev.key === 'Escape') setOpen(false);
    };
    const onRepos = () => placePopover();
    document.addEventListener('mousedown', onDoc, true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onRepos);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onRepos);
    };
  }, [open, placePopover]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`kit-compat-models-btn${open ? ' is-open' : ''}`}
        aria-label="Modelos compatíveis"
        aria-expanded={open}
        onClick={toggleOpen}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseEnter={placeHoverTip}
        onMouseLeave={hideHoverTip}
        onFocus={placeHoverTip}
        onBlur={hideHoverTip}
      >
        <IconCirclesRelation size={14} stroke={1.75} aria-hidden />
      </button>
      {hoverTip && !open
        ? ReactDOM.createPortal(
            <span
              className={`kit-compat-models-btn-tip kit-compat-models-btn-tip--portal kit-compat-models-btn-tip--${hoverTip.place}`}
              role="tooltip"
              style={{ top: hoverTip.top, left: hoverTip.left }}
            >
              Modelos compatíveis
            </span>,
            document.body
          )
        : null}
      {open && pos
        ? ReactDOM.createPortal(
            <div
              ref={popRef}
              className={`kit-compat-models-pop kit-compat-models-pop--${pos.place}`}
              style={{ top: pos.top, left: pos.left, width: pos.width }}
              role="dialog"
              aria-label="Modelos compatíveis"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="kit-compat-models-pop-head">
                <IconCirclesRelation size={15} stroke={1.75} aria-hidden />
                <span className="kit-compat-models-pop-title">Modelos compatíveis</span>
              </div>
              <div className="kit-compat-models-pop-body">
                {loading ? (
                  <div className="kit-compat-models-empty">Carregando…</div>
                ) : error ? (
                  <div className="kit-compat-models-empty is-error">{error}</div>
                ) : !modelGroups.length ? (
                  <div className="kit-compat-models-empty">Nenhum modelo vinculado a este produto.</div>
                ) : (
                  <div className="kit-compat-models-groups">
                    {modelGroups.map((g) => (
                      <div key={g.fabricante} className="kit-compat-models-group">
                        <div className="kit-compat-models-group-head">{g.fabricante}</div>
                        <ul className="kit-compat-models-list">
                          {g.names.map((name) => (
                            <li key={`${g.fabricante}-${name}`} className="kit-compat-models-item">
                              <span className="kit-compat-models-name">{name}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}

function sanitizeMaxQtyInput(raw) {
  return String(raw ?? '')
    .replace(/\D/g, '')
    .slice(0, 6);
}

/** Quantidade máx. do kit: inteiro ≥ 1 (obrigatório). */
export function isValidKitMaxQty(raw) {
  const s = String(raw ?? '').trim();
  if (!s || !/^\d+$/.test(s)) return false;
  const n = Number(s);
  return Number.isInteger(n) && n >= 1;
}

function KitMaxQtyInput({ value, invalid, productName, onChange }) {
  return (
    <input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      className={`kit-max-qty-input${invalid ? ' is-invalid' : ''}`}
      value={value ?? ''}
      placeholder="Qtd"
      aria-label={`Quantidade máxima no kit — ${productName || 'produto'}`}
      title="Quantidade máxima no kit (obrigatório, mínimo 1)"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onChange={(e) => onChange(sanitizeMaxQtyInput(e.target.value))}
    />
  );
}

/**
 * Multi-select de estoque por empresa (VW02001).
 * selected: Array<{ companyCode, stockAvailable }>
 */
export function ProductStockPicker({ productCode, productName, selected, onChange }) {
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState(null);
  const [pos, setPos] = useState(null);

  const selectedList = Array.isArray(selected) ? selected : [];
  const selectedSet = useMemo(
    () => new Set(selectedList.map((s) => String(s.companyCode || '').trim()).filter(Boolean)),
    [selectedList]
  );
  const nSel = selectedSet.size;

  const placePopover = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = 280;
    const pad = 8;
    let left = r.left;
    if (left + width > window.innerWidth - pad) left = window.innerWidth - width - pad;
    if (left < pad) left = pad;
    const spaceBelow = window.innerHeight - r.bottom;
    const preferBelow = spaceBelow > 180 || r.top < 160;
    setPos({
      left,
      top: preferBelow ? r.bottom + 6 : r.top - 6,
      place: preferBelow ? 'below' : 'above',
      width,
    });
  }, []);

  const loadStocks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchKitProductStocks(productCode);
      setRows(list);
    } catch (err) {
      setRows([]);
      setError(err?.message || 'Erro ao carregar estoque');
    } finally {
      setLoading(false);
    }
  }, [productCode]);

  const toggleOpen = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setOpen((prev) => {
      if (!prev) {
        placePopover();
        return true;
      }
      return false;
    });
  };

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      await loadStocks();
      if (!cancelled) placePopover();
    })();
    return () => {
      cancelled = true;
    };
  }, [open, loadStocks, placePopover]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (ev) => {
      if (btnRef.current?.contains(ev.target) || popRef.current?.contains(ev.target)) return;
      setOpen(false);
    };
    const onKey = (ev) => {
      if (ev.key === 'Escape') setOpen(false);
    };
    const onRepos = () => placePopover();
    document.addEventListener('mousedown', onDoc, true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onRepos);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onRepos);
    };
  }, [open, placePopover]);

  const toggleCompany = (row) => {
    const code = String(row.companyCode || '').trim();
    if (!code) return;
    const nextMap = new Map(
      selectedList.map((s) => [String(s.companyCode || '').trim(), s])
    );
    if (nextMap.has(code)) nextMap.delete(code);
    else {
      nextMap.set(code, {
        companyCode: code,
        stockAvailable: Number(row.stockAvailable) || 0,
      });
    }
    onChange(Array.from(nextMap.values()));
  };

  const label =
    nSel === 0 ? 'Estoque' : nSel === 1 ? selectedList[0].companyCode : `${nSel} emp.`;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`kit-stock-picker-btn${open ? ' is-open' : ''}${nSel ? ' has-sel' : ''}`}
        aria-label={`Estoque por empresa — ${productName || productCode}`}
        aria-expanded={open}
        title="Selecionar estoque(s) por empresa"
        onClick={toggleOpen}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <IconBuilding size={13} stroke={1.75} aria-hidden />
        <span className="kit-stock-picker-btn-label">{label}</span>
        {nSel > 0 ? <span className="kit-stock-picker-btn-n">{nSel}</span> : null}
      </button>
      {open && pos
        ? ReactDOM.createPortal(
            <div
              ref={popRef}
              className={`kit-stock-pop kit-stock-pop--${pos.place}`}
              style={{ top: pos.top, left: pos.left, width: pos.width }}
              role="dialog"
              aria-label="Estoque por empresa. O pedido irá retirar estoque de quais empresas?"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="kit-stock-pop-head">
                <IconBuilding size={14} stroke={1.75} aria-hidden />
                <div className="kit-stock-pop-head-text">
                  <span className="kit-stock-pop-title">Estoque por empresa</span>
                  <span className="kit-stock-pop-sub">
                    O pedido irá retirar estoque de quais empresas?
                  </span>
                </div>
              </div>
              <div className="kit-stock-pop-cols" aria-hidden>
                <span>Empresa</span>
                <span>Estoque</span>
              </div>
              <div className="kit-stock-pop-body">
                {loading ? (
                  <div className="kit-stock-pop-empty">Carregando…</div>
                ) : error ? (
                  <div className="kit-stock-pop-empty is-error">{error}</div>
                ) : !rows?.length ? (
                  <div className="kit-stock-pop-empty">Sem estoque ativo para este produto.</div>
                ) : (
                  <ul className="kit-stock-list">
                    {rows.map((row) => {
                      const code = String(row.companyCode || '').trim();
                      const on = selectedSet.has(code);
                      return (
                        <li key={code}>
                          <button
                            type="button"
                            className={`kit-stock-row${on ? ' is-selected' : ''}`}
                            onClick={() => toggleCompany(row)}
                            aria-pressed={on}
                          >
                            <span className={`kit-stock-check${on ? ' on' : ''}`} aria-hidden>
                              {on ? '✓' : ''}
                            </span>
                            <span className="kit-stock-company" title={code}>
                              {code}
                            </span>
                            <span className="kit-stock-qty">{row.stockAvailable ?? 0}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}

/**
 * Motivo da diminuição de quantity_max no detalhe do kit.
 * @param {{
 *   productCode: string,
 *   productName?: string,
 *   reason: 'return_stock'|'keep_with_tech'|null|undefined,
 *   stockSelected: Array<{ companyCode: string, stockAvailable?: number }>,
 *   onReasonChange: (reason: 'return_stock'|'keep_with_tech'|null) => void,
 *   onStockChange: (next: Array<{ companyCode: string, stockAvailable?: number }>) => void,
 * }} props
 */
export function KitQtyMotivoPicker({
  productCode,
  productName,
  reason,
  stockSelected,
  onReasonChange,
  onStockChange,
}) {
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState(null);
  const [pos, setPos] = useState(null);

  const selectedList = Array.isArray(stockSelected) ? stockSelected : [];
  const selectedSet = useMemo(
    () => new Set(selectedList.map((s) => String(s.companyCode || '').trim()).filter(Boolean)),
    [selectedList]
  );
  const nSel = selectedSet.size;
  const activeReason = reason === 'return_stock' || reason === 'keep_with_tech' ? reason : null;

  const placePopover = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = 280;
    const pad = 8;
    let left = r.left;
    if (left + width > window.innerWidth - pad) left = window.innerWidth - width - pad;
    if (left < pad) left = pad;
    const spaceBelow = window.innerHeight - r.bottom;
    const preferBelow = spaceBelow > 220 || r.top < 180;
    setPos({
      left,
      top: preferBelow ? r.bottom + 6 : r.top - 6,
      place: preferBelow ? 'below' : 'above',
      width,
    });
  }, []);

  const loadStocks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchKitProductStocks(productCode);
      setRows(list);
    } catch (err) {
      setRows([]);
      setError(err?.message || 'Erro ao carregar estoque');
    } finally {
      setLoading(false);
    }
  }, [productCode]);

  const toggleOpen = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setOpen((prev) => {
      if (!prev) {
        placePopover();
        return true;
      }
      return false;
    });
  };

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      if (activeReason === 'return_stock') {
        await loadStocks();
      }
      if (!cancelled) placePopover();
    })();
    return () => {
      cancelled = true;
    };
  }, [open, activeReason, loadStocks, placePopover]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (ev) => {
      if (btnRef.current?.contains(ev.target) || popRef.current?.contains(ev.target)) return;
      setOpen(false);
    };
    const onKey = (ev) => {
      if (ev.key === 'Escape') setOpen(false);
    };
    const onRepos = () => placePopover();
    document.addEventListener('mousedown', onDoc, true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onRepos);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onRepos);
    };
  }, [open, placePopover]);

  const pickReturnStock = () => {
    onReasonChange('return_stock');
    loadStocks();
  };

  const pickKeepWithTech = () => {
    onReasonChange('keep_with_tech');
    onStockChange([]);
    setOpen(false);
  };

  const toggleCompany = (row) => {
    const code = String(row.companyCode || '').trim();
    if (!code) return;
    const nextMap = new Map(
      selectedList.map((s) => [String(s.companyCode || '').trim(), s])
    );
    if (nextMap.has(code)) nextMap.delete(code);
    else {
      nextMap.set(code, {
        companyCode: code,
        stockAvailable: Number(row.stockAvailable) || 0,
      });
    }
    onStockChange(Array.from(nextMap.values()));
  };

  let label = 'Motivo';
  if (activeReason === 'keep_with_tech') label = 'Com técnico';
  else if (activeReason === 'return_stock') {
    label = nSel === 0 ? 'Devolver' : nSel === 1 ? selectedList[0].companyCode : `${nSel} emp.`;
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`kit-motivo-picker-btn${open ? ' is-open' : ''}${activeReason ? ' has-sel' : ''}`}
        aria-label={`Motivo da diminuição — ${productName || productCode}`}
        aria-expanded={open}
        title="Motivo da diminuição no kit"
        onClick={toggleOpen}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <span className="kit-motivo-picker-btn-label">{label}</span>
        {activeReason === 'return_stock' && nSel > 0 ? (
          <span className="kit-motivo-picker-btn-n">{nSel}</span>
        ) : null}
      </button>
      {open && pos
        ? ReactDOM.createPortal(
            <div
              ref={popRef}
              className={`kit-stock-pop kit-motivo-pop kit-stock-pop--${pos.place}`}
              style={{ top: pos.top, left: pos.left, width: pos.width }}
              role="dialog"
              aria-label="Motivo da diminuição"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="kit-stock-pop-head">
                <div className="kit-stock-pop-head-text">
                  <span className="kit-stock-pop-title">Motivo</span>
                  <span className="kit-stock-pop-sub">Como tratar a peça removida do kit?</span>
                </div>
              </div>
              <div className="kit-motivo-options">
                <button
                  type="button"
                  className={`kit-motivo-option${activeReason === 'return_stock' ? ' is-active' : ''}`}
                  onClick={pickReturnStock}
                >
                  <IconBuilding size={15} stroke={1.75} aria-hidden />
                  <span>Devolver ao estoque</span>
                </button>
                <button
                  type="button"
                  className={`kit-motivo-option${activeReason === 'keep_with_tech' ? ' is-active' : ''}`}
                  onClick={pickKeepWithTech}
                >
                  <IconUserCheck size={15} stroke={1.75} aria-hidden />
                  <span>Deixar com técnico</span>
                </button>
              </div>
              {activeReason === 'return_stock' ? (
                <>
                  <div className="kit-motivo-return-head">
                    <span className="kit-stock-pop-sub">
                      Devolver para estoque de qual empresa?
                    </span>
                  </div>
                  <div className="kit-stock-pop-cols" aria-hidden>
                    <span>Empresa</span>
                    <span>Estoque</span>
                  </div>
                  <div className="kit-stock-pop-body kit-motivo-return-body">
                    {loading ? (
                      <div className="kit-stock-pop-empty">Carregando…</div>
                    ) : error ? (
                      <div className="kit-stock-pop-empty is-error">{error}</div>
                    ) : !rows?.length ? (
                      <div className="kit-stock-pop-empty">Sem estoque ativo para este produto.</div>
                    ) : (
                      <ul className="kit-stock-list">
                        {rows.map((row) => {
                          const code = String(row.companyCode || '').trim();
                          const on = selectedSet.has(code);
                          return (
                            <li key={code}>
                              <button
                                type="button"
                                className={`kit-stock-row${on ? ' is-selected' : ''}`}
                                onClick={() => toggleCompany(row)}
                                aria-pressed={on}
                              >
                                <span className={`kit-stock-check${on ? ' on' : ''}`} aria-hidden>
                                  {on ? '✓' : ''}
                                </span>
                                <span className="kit-stock-company" title={code}>
                                  {code}
                                </span>
                                <span className="kit-stock-qty">{row.stockAvailable ?? 0}</span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </>
              ) : null}
            </div>,
            document.body
          )
        : null}
    </>
  );
}

/**
 * @param {{
 *   selectedMap: Map<string, object>,
 *   onChangeSelected: (next: Map<string, object>) => void,
 *   excludeProductCodes?: string[],
 * }} props
 */
export default function KitCreateProductsStep({
  selectedMap,
  onChangeSelected,
  excludeProductCodes = [],
}) {
  const [fabricantesCat, setFabricantesCat] = useState([]);
  const [tecnologiasCat, setTecnologiasCat] = useState([]);
  const [equipamentosCat, setEquipamentosCat] = useState([]);
  const [fabCatReady, setFabCatReady] = useState(false);
  const [tecCatReady, setTecCatReady] = useState(false);
  const [eqpCatReady, setEqpCatReady] = useState(false);
  const [catalogError, setCatalogError] = useState(null);

  const [fabricantes, setFabricantes] = useState([]);
  const [tecnologias, setTecnologias] = useState([]);
  const [equipamentos, setEquipamentos] = useState([]);
  const [busca, setBusca] = useState('');
  const [buscaDeb, setBuscaDeb] = useState('');
  const [fabOpen, setFabOpen] = useState(false);
  const [tecOpen, setTecOpen] = useState(false);
  const [eqpOpen, setEqpOpen] = useState(false);
  const [fabSearch, setFabSearch] = useState('');
  const [tecSearch, setTecSearch] = useState('');
  const [eqpSearch, setEqpSearch] = useState('');

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsRefreshing, setProductsRefreshing] = useState(false);
  const [productsError, setProductsError] = useState(null);

  const [collapsed, setCollapsed] = useState(() => new Set());
  const [linkedCollapsed, setLinkedCollapsed] = useState(() => new Set());

  const fabBtnRef = useRef(null);
  const tecBtnRef = useRef(null);
  const eqpBtnRef = useRef(null);
  const productsLenRef = useRef(0);
  const fabLayout = useEcFilterPopoverLayout(fabBtnRef, fabOpen, 420, 380);
  const tecLayout = useEcFilterPopoverLayout(tecBtnRef, tecOpen, 420, 400);
  const eqpLayout = useEcFilterPopoverLayout(eqpBtnRef, eqpOpen, 460, 400);
  const fabPopoverStyle = buildEcFilterPopoverStyle(fabLayout);
  const tecPopoverStyle = buildEcFilterPopoverStyle(tecLayout);
  const eqpPopoverStyle = buildEcFilterPopoverStyle(eqpLayout);

  const catalogLoading = !fabCatReady || !tecCatReady || !eqpCatReady;

  const fabItems = useMemo(
    () => fabricantesCat.map((f) => ({ value: f.cod, label: f.name })),
    [fabricantesCat]
  );

  const filteredFabItems = useMemo(() => {
    const q = fabSearch.trim().toLowerCase();
    if (!q) return fabItems;
    return fabItems.filter((i) => String(i.label).toLowerCase().includes(q));
  }, [fabItems, fabSearch]);

  const eqpItems = useMemo(
    () => equipamentosCat.map((e) => ({ value: e.cod, label: e.name })),
    [equipamentosCat]
  );

  const filteredEqpItems = useMemo(() => {
    const q = eqpSearch.trim().toLowerCase();
    if (!q) return eqpItems;
    return eqpItems.filter((i) => String(i.label).toLowerCase().includes(q));
  }, [eqpItems, eqpSearch]);

  useEffect(() => {
    const t = setTimeout(() => setBuscaDeb(busca.trim()), 320);
    return () => clearTimeout(t);
  }, [busca]);

  /** Fabricante: opções reduzidas por tecnologia + equipamento + busca */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCatalogError(null);
      try {
        const fabs = await fetchKitFabricantes({
          subgrupos: tecnologias,
          equipamentos,
          q: buscaDeb,
        });
        if (cancelled) return;
        setFabricantesCat(fabs);
        const valid = new Set(fabs.map((f) => f.cod));
        setFabricantes((prev) => {
          const next = prev.filter((c) => valid.has(c));
          return next.length === prev.length ? prev : next;
        });
        setFabCatReady(true);
      } catch (err) {
        if (!cancelled) {
          setCatalogError(err?.message || 'Erro ao carregar fabricantes');
          setFabCatReady(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tecnologias, equipamentos, buscaDeb]);

  /** Tecnologia: opções reduzidas por fabricante + equipamento + busca */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const tecs = await fetchKitTecnologias({
          marcas: fabricantes,
          equipamentos,
          q: buscaDeb,
        });
        if (cancelled) return;
        setTecnologiasCat(tecs);
        const valid = new Set();
        for (const g of tecs) {
          for (const s of g.subgroups || []) {
            valid.add(s.cod_subgroup);
          }
        }
        setTecnologias((prev) => {
          const next = prev.filter((c) => valid.has(c));
          return next.length === prev.length ? prev : next;
        });
        setTecCatReady(true);
      } catch (err) {
        if (!cancelled) {
          setCatalogError(err?.message || 'Erro ao carregar tecnologias');
          setTecCatReady(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fabricantes, equipamentos, buscaDeb]);

  /** Equipamento: modelos compatíveis com fabricante + tecnologia + busca */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchKitEquipamentos({
          marcas: fabricantes,
          subgrupos: tecnologias,
          q: buscaDeb,
        });
        if (cancelled) return;
        setEquipamentosCat(list);
        const valid = new Set(list.map((e) => e.cod));
        setEquipamentos((prev) => {
          const next = prev.filter((c) => valid.has(c));
          return next.length === prev.length ? prev : next;
        });
        setEqpCatReady(true);
      } catch (err) {
        if (!cancelled) {
          setCatalogError(err?.message || 'Erro ao carregar equipamentos');
          setEqpCatReady(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fabricantes, tecnologias, buscaDeb]);

  useEffect(() => {
    if (!fabOpen) setFabSearch('');
  }, [fabOpen]);

  useEffect(() => {
    if (!tecOpen) setTecSearch('');
  }, [tecOpen]);

  useEffect(() => {
    if (!eqpOpen) setEqpSearch('');
  }, [eqpOpen]);

  useEffect(() => {
    if (!fabOpen) return undefined;
    const onDoc = (e) => {
      const pop = document.querySelector('[data-kit-fab-popover]');
      if (pop?.contains(e.target) || fabBtnRef.current?.contains(e.target)) return;
      setFabOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [fabOpen]);

  useEffect(() => {
    if (!tecOpen) return undefined;
    const onDoc = (e) => {
      const pop = document.querySelector('[data-kit-tec-popover]');
      if (pop?.contains(e.target) || tecBtnRef.current?.contains(e.target)) return;
      setTecOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [tecOpen]);

  useEffect(() => {
    if (!eqpOpen) return undefined;
    const onDoc = (e) => {
      const pop = document.querySelector('[data-kit-eqp-popover]');
      if (pop?.contains(e.target) || eqpBtnRef.current?.contains(e.target)) return;
      setEqpOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [eqpOpen]);

  const canShowList =
    fabricantes.length > 0 ||
    tecnologias.length > 0 ||
    equipamentos.length > 0 ||
    buscaDeb.length >= 1;

  useEffect(() => {
    if (!canShowList) {
      setProducts([]);
      productsLenRef.current = 0;
      setProductsError(null);
      setProductsLoading(false);
      setProductsRefreshing(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      const soft = productsLenRef.current > 0;
      if (soft) setProductsRefreshing(true);
      else setProductsLoading(true);
      setProductsError(null);
      try {
        const list = await fetchKitProdutos({
          marcas: fabricantes,
          subgrupos: tecnologias,
          equipamentos,
          q: buscaDeb,
        });
        if (cancelled) return;
        setProducts(list);
        productsLenRef.current = list.length;
      } catch (err) {
        if (!cancelled) {
          if (!soft) {
            setProducts([]);
            productsLenRef.current = 0;
          }
          setProductsError(err?.message || 'Erro ao carregar produtos');
        }
      } finally {
        if (!cancelled) {
          setProductsLoading(false);
          setProductsRefreshing(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canShowList, fabricantes, tecnologias, equipamentos, buscaDeb]);

  const excludeSet = useMemo(
    () =>
      new Set(
        (excludeProductCodes || []).map((c) => String(c || '').trim()).filter(Boolean)
      ),
    [excludeProductCodes]
  );

  const visibleProducts = useMemo(
    () =>
      excludeSet.size
        ? products.filter((p) => !excludeSet.has(String(p.cod_product || '').trim()))
        : products,
    [products, excludeSet]
  );

  const byBrand = useMemo(() => {
    const map = new Map();
    for (const p of visibleProducts) {
      const codFab = p.cod_fabricante || '_';
      const nomeFab = p.nome_fabricante || 'Sem fabricante';
      if (!map.has(codFab)) {
        map.set(codFab, {
          cod_fabricante: codFab,
          brand_nome: nomeFab,
          products: [],
        });
      }
      map.get(codFab).products.push(p);
    }
    const list = Array.from(map.values());
    list.sort((a, b) => String(a.brand_nome).localeCompare(String(b.brand_nome), 'pt-BR'));
    for (const g of list) {
      g.products.sort((a, b) =>
        String(a.nome_product).localeCompare(String(b.nome_product), 'pt-BR')
      );
    }
    return list;
  }, [visibleProducts]);

  const selectedList = useMemo(() => Array.from(selectedMap.values()), [selectedMap]);

  const selectedByBrand = useMemo(() => {
    const map = new Map();
    for (const p of selectedList) {
      const codFab = p.cod_fabricante || '_';
      const nomeFab = p.nome_fabricante || 'Sem fabricante';
      if (!map.has(codFab)) {
        map.set(codFab, { cod_fabricante: codFab, brand_nome: nomeFab, products: [] });
      }
      map.get(codFab).products.push(p);
    }
    const list = Array.from(map.values());
    list.sort((a, b) => String(a.brand_nome).localeCompare(String(b.brand_nome), 'pt-BR'));
    for (const g of list) {
      g.products.sort((a, b) =>
        String(a.nome_product).localeCompare(String(b.nome_product), 'pt-BR')
      );
    }
    return list;
  }, [selectedList]);

  const hasFilters =
    fabricantes.length > 0 || tecnologias.length > 0 || equipamentos.length > 0 || busca !== '';

  const toggleProduct = useCallback(
    (product) => {
      const cod = String(product.cod_product || '').trim();
      if (!cod) return;
      const next = new Map(selectedMap);
      if (next.has(cod)) next.delete(cod);
      else
        next.set(cod, {
          ...product,
          max_qty: product.max_qty ?? '',
          stock_sources: Array.isArray(product.stock_sources) ? product.stock_sources : [],
        });
      onChangeSelected(next);
    },
    [selectedMap, onChangeSelected]
  );

  const setProductMaxQty = useCallback(
    (cod, maxQty) => {
      const key = String(cod || '').trim();
      if (!key || !selectedMap.has(key)) return;
      const prev = selectedMap.get(key);
      const next = new Map(selectedMap);
      next.set(key, { ...prev, max_qty: maxQty });
      onChangeSelected(next);
    },
    [selectedMap, onChangeSelected]
  );

  const setProductStockSources = useCallback(
    (cod, stockSources) => {
      const key = String(cod || '').trim();
      if (!key || !selectedMap.has(key)) return;
      const prev = selectedMap.get(key);
      const next = new Map(selectedMap);
      next.set(key, {
        ...prev,
        stock_sources: Array.isArray(stockSources) ? stockSources : [],
      });
      onChangeSelected(next);
    },
    [selectedMap, onChangeSelected]
  );

  const toggleGroupSelection = useCallback(
    (g) => {
      const codes = g.products.map((p) => p.cod_product);
      const allOn = codes.every((c) => selectedMap.has(c));
      const next = new Map(selectedMap);
      if (allOn) {
        codes.forEach((c) => next.delete(c));
      } else {
        g.products.forEach((p) => {
          const existing = next.get(p.cod_product);
          next.set(p.cod_product, {
            ...p,
            max_qty: existing?.max_qty ?? '',
            stock_sources: Array.isArray(existing?.stock_sources) ? existing.stock_sources : [],
          });
        });
      }
      onChangeSelected(next);
    },
    [selectedMap, onChangeSelected]
  );

  const clearBrandSelected = useCallback(
    (g) => {
      const next = new Map(selectedMap);
      g.products.forEach((p) => next.delete(p.cod_product));
      onChangeSelected(next);
    },
    [selectedMap, onChangeSelected]
  );

  const clearFilters = () => {
    setFabricantes([]);
    setTecnologias([]);
    setEquipamentos([]);
    setBusca('');
    setBuscaDeb('');
  };

  const handleToggleFab = (value) => {
    setFabricantes((prev) =>
      prev.includes(value) ? prev.filter((m) => m !== value) : [...prev, value]
    );
  };

  const handleFabBulkToggle = () => {
    const visible = filteredFabItems.map((i) => i.value);
    if (!visible.length) return;
    const allSelected = visible.every((v) => fabricantes.includes(v));
    setFabricantes((prev) =>
      allSelected
        ? prev.filter((m) => !visible.includes(m))
        : Array.from(new Set([...prev, ...visible]))
    );
  };

  const handleToggleEqp = (value) => {
    setEquipamentos((prev) =>
      prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value]
    );
  };

  const handleEqpBulkToggle = () => {
    const visible = filteredEqpItems.map((i) => i.value);
    if (!visible.length) return;
    const allSelected = visible.every((v) => equipamentos.includes(v));
    setEquipamentos((prev) =>
      allSelected
        ? prev.filter((c) => !visible.includes(c))
        : Array.from(new Set([...prev, ...visible]))
    );
  };

  /** Tecnología uses codes as selectedTecs; panel shows codes — need label map in panel */
  // Build groups for Deq with display names as itens, keep parallel name→code map
  const nameToCode = useMemo(() => {
    const map = new Map();
    for (const g of tecnologiasCat) {
      for (const s of g.subgroups || []) {
        // unique key if duplicate names: prefer first
        const label = s.nome_subgroup;
        if (!map.has(label)) map.set(label, s.cod_subgroup);
        map.set(`${g.cod_group}::${label}`, s.cod_subgroup);
      }
    }
    return map;
  }, [tecnologiasCat]);

  const codeToName = useMemo(() => {
    const map = new Map();
    for (const g of tecnologiasCat) {
      for (const s of g.subgroups || []) {
        map.set(s.cod_subgroup, s.nome_subgroup);
      }
    }
    return map;
  }, [tecnologiasCat]);

  const tecGroupsByName = useMemo(
    () =>
      tecnologiasCat.map((g) => ({
        grupo: g.nome_group,
        itens: (g.subgroups || []).map((s) => s.nome_subgroup),
        cods: (g.subgroups || []).map((s) => s.cod_subgroup),
      })),
    [tecnologiasCat]
  );

  /** selected tecnologias as codes; panel needs names */
  const selectedTecNames = useMemo(
    () => tecnologias.map((c) => codeToName.get(c) || c).filter(Boolean),
    [tecnologias, codeToName]
  );

  const handleToggleTecName = (nome) => {
    // find code - prefer matching in open group context later; global first match
    let code = null;
    for (const g of tecGroupsByName) {
      const idx = g.itens.indexOf(nome);
      if (idx >= 0) {
        code = g.cods[idx];
        break;
      }
    }
    if (!code) code = nameToCode.get(nome);
    if (!code) return;
    setTecnologias((prev) =>
      prev.includes(code) ? prev.filter((t) => t !== code) : [...prev, code]
    );
  };

  const handleToggleGrupoName = (grupo, itens) => {
    const codes = [];
    const g = tecGroupsByName.find((x) => x.grupo === grupo);
    if (g) {
      for (let i = 0; i < g.itens.length; i += 1) {
        if (itens.includes(g.itens[i])) codes.push(g.cods[i]);
      }
    } else {
      itens.forEach((n) => {
        const c = nameToCode.get(n);
        if (c) codes.push(c);
      });
    }
    const allSelected = codes.length > 0 && codes.every((c) => tecnologias.includes(c));
    setTecnologias((prev) => {
      if (allSelected) return prev.filter((t) => !codes.includes(t));
      return Array.from(new Set([...prev, ...codes]));
    });
  };

  const handleSetTecNames = (names) => {
    const codes = [];
    for (const n of names) {
      for (const g of tecGroupsByName) {
        const idx = g.itens.indexOf(n);
        if (idx >= 0) {
          codes.push(g.cods[idx]);
          break;
        }
      }
    }
    setTecnologias(Array.from(new Set(codes)));
  };

  const toggleCollapsed = (key) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleLinkedCollapsed = (key) => {
    setLinkedCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (catalogLoading) {
    return <div className="kit-products-loading">Carregando catálogo…</div>;
  }

  if (catalogError) {
    return <div className="kit-products-error">{catalogError}</div>;
  }

  return (
    <div className="mscad-equip-panel kit-products-equip-panel">
      <div className="deq-filters mscad-filters">
        <EcClickUpFilterChip
          anchorRef={fabBtnRef}
          label="Fabricante"
          value={fabricantes.length > 0 ? String(fabricantes.length) : null}
          icon={IconBuilding}
          hasSelection={fabricantes.length > 0}
          isOpen={fabOpen}
          ariaLabel="Filtrar por fabricante"
          onClick={() => {
            setTecOpen(false);
            setEqpOpen(false);
            setFabOpen((o) => !o);
          }}
        />
        {fabOpen && fabPopoverStyle
          ? ReactDOM.createPortal(
              <div
                data-kit-fab-popover
                className={`${EC_FILTER_POPOVER_CLASS} board-search-filters-popover--simple board-search-filters-popover--cidade deq-filter-popover kit-filter-popover`}
                style={withPopoverStyle(fabPopoverStyle)}
                role="dialog"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <BoardSearchSimplePickerPanel
                  searchPlaceholder="Buscar fabricante..."
                  searchTerm={fabSearch}
                  onSearchChange={setFabSearch}
                  items={filteredFabItems}
                  getKey={(item) => item.value}
                  getLabel={(item) => item.label}
                  selectedKeys={fabricantes}
                  onToggle={(item) => handleToggleFab(item.value)}
                  emptyMessage={
                    hasFilters
                      ? 'Nenhum fabricante com os filtros atuais'
                      : 'Nenhum fabricante'
                  }
                  listAriaLabel="Fabricantes"
                  bulkMenuEnabled
                  onBulkToggleAll={handleFabBulkToggle}
                />
              </div>,
              document.body
            )
          : null}

        <EcClickUpFilterChip
          anchorRef={tecBtnRef}
          label="Tecnologia"
          value={tecnologias.length > 0 ? String(tecnologias.length) : null}
          icon={IconStack2}
          hasSelection={tecnologias.length > 0}
          isOpen={tecOpen}
          ariaLabel="Filtrar por tecnologia"
          onClick={() => {
            setFabOpen(false);
            setEqpOpen(false);
            setTecOpen((o) => !o);
          }}
        />
        {tecOpen && tecPopoverStyle
          ? ReactDOM.createPortal(
              <div
                data-kit-tec-popover
                className={`${EC_FILTER_POPOVER_CLASS} board-search-filters-popover--simple board-search-filters-popover--cidade deq-filter-popover kit-filter-popover`}
                style={withPopoverStyle(tecPopoverStyle)}
                role="dialog"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <DeqEquipTecnologiaFilterPanel
                  groups={tecGroupsByName}
                  searchTerm={tecSearch}
                  onSearchChange={setTecSearch}
                  selectedTecs={selectedTecNames}
                  onToggleTec={handleToggleTecName}
                  onToggleGrupo={handleToggleGrupoName}
                  onSetTecs={handleSetTecNames}
                />
              </div>,
              document.body
            )
          : null}

        <EcClickUpFilterChip
          anchorRef={eqpBtnRef}
          label="Equipamento"
          value={equipamentos.length > 0 ? String(equipamentos.length) : null}
          icon={IconPrinter}
          hasSelection={equipamentos.length > 0}
          isOpen={eqpOpen}
          ariaLabel="Filtrar por equipamento"
          className="kit-eqp-filter-chip"
          tooltip={
            <span className="kit-eqp-tip">
              <span className="kit-eqp-tip-title">Equipamentos compatíveis</span>
              <span className="kit-eqp-tip-desc">
                Selecione um equipamento, e obtenha apenas produtos compatíveis na listagem.
              </span>
            </span>
          }
          tooltipMultiline
          onClick={() => {
            setFabOpen(false);
            setTecOpen(false);
            setEqpOpen((o) => !o);
          }}
        />
        {eqpOpen && eqpPopoverStyle
          ? ReactDOM.createPortal(
              <div
                data-kit-eqp-popover
                className={`${EC_FILTER_POPOVER_CLASS} board-search-filters-popover--simple board-search-filters-popover--cidade deq-filter-popover kit-filter-popover`}
                style={withPopoverStyle(eqpPopoverStyle)}
                role="dialog"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <BoardSearchSimplePickerPanel
                  searchPlaceholder="Buscar equipamento..."
                  searchTerm={eqpSearch}
                  onSearchChange={setEqpSearch}
                  items={filteredEqpItems}
                  getKey={(item) => item.value}
                  getLabel={(item) => item.label}
                  selectedKeys={equipamentos}
                  onToggle={(item) => handleToggleEqp(item.value)}
                  emptyMessage={
                    hasFilters
                      ? 'Nenhum equipamento com os filtros atuais'
                      : 'Nenhum equipamento'
                  }
                  listAriaLabel="Equipamentos (modelos)"
                  bulkMenuEnabled
                  onBulkToggleAll={handleEqpBulkToggle}
                />
              </div>,
              document.body
            )
          : null}

        <BoardToolbarExpandSearch
          value={busca}
          onChange={setBusca}
          placeholder="Buscar produto..."
          ariaLabel="Buscar produto"
          forceExpanded
        />

        {hasFilters ? (
          <button type="button" className="deq-clear-filters" onClick={clearFilters}>
            Limpar filtros
          </button>
        ) : null}

        {canShowList ? (
          <span className="deq-filter-count kit-filter-count">
            {productsRefreshing ? '…' : null}
            {visibleProducts.length} {visibleProducts.length === 1 ? 'produto' : 'produtos'}
          </span>
        ) : null}
      </div>

      <div className="deq-main mscad-main">
        <div className="deq-available mscad-available">
          <div
            className={`deq-scroll mscad-scroll mscad-scroll--available${
              productsRefreshing ? ' kit-products-scroll--refreshing' : ''
            }`}
          >
            {!canShowList ? (
              <FiltersEmptyState />
            ) : productsLoading ? (
              <div className="deq-empty">
                <div className="deq-empty-title">Carregando produtos…</div>
              </div>
            ) : productsError && byBrand.length === 0 ? (
              <div className="deq-empty">
                <div className="deq-empty-title">Erro</div>
                <div className="deq-empty-hint">{productsError}</div>
              </div>
            ) : byBrand.length === 0 ? (
              <div className="deq-empty">
                <div className="deq-empty-title">Nenhum resultado encontrado</div>
                <div className="deq-empty-hint">
                  {products.length > 0 && excludeSet.size
                    ? 'Os produtos desta busca já estão no kit deste técnico. Ajuste os filtros.'
                    : 'Combinação de fabricante, tecnologia, equipamento e busca sem produtos. Ajuste ou limpe os filtros.'}
                </div>
              </div>
            ) : (
              byBrand.map((g) => {
                const key = g.cod_fabricante;
                const isCollapsed = collapsed.has(key);
                const selectedCount = g.products.filter((p) =>
                  selectedMap.has(p.cod_product)
                ).length;
                const allOn = g.products.every((p) => selectedMap.has(p.cod_product));
                return (
                  <div key={key}>
                    <button
                      type="button"
                      className="deq-brand-head"
                      onClick={() => toggleCollapsed(key)}
                    >
                      <span className="deq-lead-slot deq-brand-chevron" aria-hidden>
                        {isCollapsed ? (
                          <ChevronRight size={13} strokeWidth={2} />
                        ) : (
                          <ChevronDown size={13} strokeWidth={2} />
                        )}
                      </span>
                      <MarcaAvatar fabricante={g.brand_nome} />
                      <span className="deq-brand-label">{g.brand_nome}</span>
                      <span className="deq-brand-meta">
                        {g.products.length}
                        {selectedCount > 0 ? ` · ${selectedCount} sel.` : ''}
                      </span>
                      <span
                        role="button"
                        tabIndex={0}
                        className="deq-brand-sel-all"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleGroupSelection(g);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleGroupSelection(g);
                          }
                        }}
                      >
                        {allOn ? 'rem. todos' : 'sel. todos'}
                      </span>
                    </button>
                    {!isCollapsed
                      ? g.products.map((p) => {
                          const on = selectedMap.has(p.cod_product);
                          return (
                            <div
                              key={p.cod_product}
                              className={`deq-model-row${on ? ' deq-model-row--linked' : ''}`}
                              onClick={() => toggleProduct(p)}
                            >
                              <KitCheckbox
                                checked={on}
                                onClick={() => toggleProduct(p)}
                                title={p.nome_product}
                              />
                              <span className="deq-model-name" title={p.nome_product}>
                                {p.nome_product}
                              </span>
                              <ProductSubmeta cod={p.cod_product} referencia={p.referencia} />
                            </div>
                          );
                        })
                      : null}
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="deq-linked-panel mscad-linked-panel">
          <div className="deq-linked-head">
            <span className="deq-linked-title">No kit · {selectedList.length}</span>
          </div>
          <div className="deq-scroll mscad-scroll mscad-scroll--linked">
            {selectedByBrand.length === 0 ? (
              <div className="deq-empty">
                <div className="deq-empty-title">Nenhum produto no kit</div>
                <div className="deq-empty-hint">Selecione à esquerda para incluir.</div>
              </div>
            ) : (
              selectedByBrand.map((g) => {
                const key = `linked-${g.cod_fabricante}`;
                const isLinkedCollapsed = linkedCollapsed.has(g.cod_fabricante);
                return (
                  <div key={key}>
                    <button
                      type="button"
                      className="deq-brand-head deq-brand-head--linked"
                      onClick={() => toggleLinkedCollapsed(g.cod_fabricante)}
                    >
                      <span className="deq-lead-slot deq-brand-chevron" aria-hidden>
                        {isLinkedCollapsed ? (
                          <ChevronRight size={13} strokeWidth={2} />
                        ) : (
                          <ChevronDown size={13} strokeWidth={2} />
                        )}
                      </span>
                      <MarcaAvatar fabricante={g.brand_nome} />
                      <span className="deq-brand-label">{g.brand_nome}</span>
                      <span className="deq-brand-meta">{g.products.length}</span>
                      <span
                        role="button"
                        tabIndex={0}
                        className="mscad-brand-clear-all"
                        title="Remover todos deste fabricante"
                        onClick={(e) => {
                          e.stopPropagation();
                          clearBrandSelected(g);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            clearBrandSelected(g);
                          }
                        }}
                      >
                        ×
                      </span>
                    </button>
                    {!isLinkedCollapsed
                      ? g.products.map((p) => {
                          const qty = p.max_qty ?? '';
                          const qtyInvalid = !isValidKitMaxQty(qty);
                          const stockSources = Array.isArray(p.stock_sources) ? p.stock_sources : [];
                          return (
                            <div
                              key={`sel-${p.cod_product}`}
                              className="deq-model-row deq-model-row--linked kit-linked-product-row"
                              onClick={() => toggleProduct(p)}
                            >
                              <KitCheckbox
                                checked
                                onClick={() => toggleProduct(p)}
                                title={p.nome_product}
                              />
                              <span className="deq-model-name" title={p.nome_product}>
                                {p.nome_product}
                              </span>
                              <KitMaxQtyInput
                                value={qty}
                                invalid={qtyInvalid}
                                productName={p.nome_product}
                                onChange={(v) => setProductMaxQty(p.cod_product, v)}
                              />
                              <ProductStockPicker
                                productCode={p.cod_product}
                                productName={p.nome_product}
                                selected={stockSources}
                                onChange={(list) => setProductStockSources(p.cod_product, list)}
                              />
                              <ProductSubmeta cod={p.cod_product} referencia={p.referencia} />
                            </div>
                          );
                        })
                      : null}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
